import { networkIconFor, radius } from '@alison/ui';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { ThumbnailPreview } from '../creatives/ThumbnailPreview.js';
import { ELLIPSIS_SX } from '../creatives/utils.js';
import { AdjustConfiguration } from './AdjustConfiguration.js';
import { BrandGuidelinesSection } from './BrandGuidelinesSection.js';
import { CreativeAttributesAnalysis } from './CreativeAttributesAnalysis.js';
import { CreativeScorePanel } from './CreativeScorePanel.js';
import { RecommendationsSection } from './RecommendationsSection.js';
import { RescoringOverlay } from './RescoringOverlay.js';
import { RunNavigator } from './RunNavigator.js';
import { readable, toneOf } from './ScoreVisuals.js';
import { SegmentedControl } from './SegmentedControl.js';
import { creativeLine, primaryKpi, scoredOnLine, scoringModeOf } from './scoredBatch.js';
import { type CreativeDrawerProps, useCreativeDrawer } from './useCreativeDrawer.js';

const DRAWER_WIDTH = 1180;
const THUMB_HEIGHT = 128;
const THUMB_WIDTH = 228;

const BADGE_SX = {
  position: 'absolute',
  top: 8,
  left: 8,
  px: 1.5,
  py: 0.25,
  borderRadius: 1,
  fontSize: 10,
  fontWeight: 700,
  color: 'common.white',
  bgcolor: 'rgba(0, 0, 0, 0.62)',
  pointerEvents: 'none',
};

const PLATFORM_SX = {
  flex: '1 1 0',
  minWidth: 0,
  gap: 1.5,
  px: 2.5,
  py: 2,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const PAGER_SX = {
  gap: 1,
  alignItems: 'center',
  px: 1,
  py: 0.5,
  borderRadius: `${radius.pill}px`,
  border: '1px solid',
  borderColor: 'divider',
};

export function CreativeDrawer({
  onClose,
  ...opened
}: CreativeDrawerProps & { onClose: () => void }): ReactNode {
  const theme = useTheme();
  const {
    batch,
    creative,
    creativeCount,
    creativeAt,
    file,
    scores,
    network,
    halves,
    entered,
    runs,
    runAt,
    busy,
    failed,
    openNetwork,
    openRun,
    stepCreative,
    rescore,
  } = useCreativeDrawer(opened);

  return (
    <Drawer
      anchor="right"
      open={opened.open && creative !== undefined}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: `min(${DRAWER_WIDTH}px, 100vw)` } } }}
      data-testid="creative-drawer"
    >
      {creative === undefined ? null : (
        <Stack sx={{ height: '100%', position: 'relative', bgcolor: 'background.page' }}>
          {busy ? <RescoringOverlay /> : null}
          <Stack sx={{ gap: 3, px: 5, py: 3, bgcolor: 'background.paper' }}>
            <Stack direction="row" sx={{ gap: 2, alignItems: 'flex-start' }}>
              <IconButton aria-label="Back" onClick={onClose} size="small" sx={{ mt: 0.25 }}>
                <ArrowBackOutlined sx={{ fontSize: 20 }} />
              </IconButton>
              <Stack sx={{ gap: 0.25, flex: 1, minWidth: 0 }}>
                <Typography variant="h3" sx={{ fontSize: 20 }}>
                  {creative.name}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.muted' }}>
                  {[
                    `Creative ${creativeAt + 1} of ${creativeCount}`,
                    creativeLine(creative),
                    scoredOnLine(batch),
                  ]
                    .filter((part) => part != null && part !== '')
                    .join(' · ')}
                </Typography>
              </Stack>
              <Stack direction="row" sx={PAGER_SX}>
                <IconButton
                  aria-label="Previous creative"
                  size="small"
                  disabled={creativeAt <= 0}
                  onClick={() => stepCreative(-1)}
                >
                  <ArrowBackOutlined sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography sx={{ fontSize: 13 }}>
                  {creativeAt + 1} / {creativeCount}
                </Typography>
                <IconButton
                  aria-label="Next creative"
                  size="small"
                  disabled={creativeAt >= creativeCount - 1}
                  onClick={() => stepCreative(1)}
                >
                  <ArrowForwardOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
              <IconButton aria-label="Close" onClick={onClose} size="small">
                <CloseOutlined sx={{ fontSize: 20 }} />
              </IconButton>
            </Stack>

            <Stack
              direction="row"
              data-testid="platform-scores"
              sx={{ gap: 2, alignItems: 'stretch', overflowX: 'auto' }}
            >
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <ThumbnailPreview
                  name={creative.name}
                  size={THUMB_HEIGHT}
                  width={THUMB_WIDTH}
                  thumbnailUrl={file?.url ?? null}
                  previewUrl={file?.previewUrl ?? null}
                  duration={creative.duration}
                  controls
                />
                <Typography sx={BADGE_SX}>{creative.creativeType.toUpperCase()}</Typography>
              </Box>

              {scores.map((platform) => {
                const Icon = networkIconFor(platform.slug, platform.label);
                return (
                  <Stack key={platform.networkId} sx={PLATFORM_SX}>
                    <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                      {Icon === undefined ? null : <Icon sx={{ fontSize: 20 }} />}
                      <Typography sx={{ fontSize: 15, flex: 1, ...ELLIPSIS_SX }}>
                        {platform.label}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 22, fontWeight: 500, color: toneOf(theme, platform.score) }}
                      >
                        {readable(platform.score)}
                      </Typography>
                    </Stack>
                    <Divider />
                    <Stack sx={{ gap: 0.75 }}>
                      {platform.halves.map((half) => (
                        <Stack key={half.label} direction="row" sx={{ gap: 2 }}>
                          <Typography sx={{ fontSize: 13, color: 'text.muted', flex: 1 }}>
                            {half.label}
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: toneOf(theme, half.value) }}>
                            {readable(half.value)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>

          <Stack
            sx={{
              gap: 5,
              px: 5,
              py: 3,
              flex: 1,
              minHeight: 0,
              overflowY: busy ? 'hidden' : 'auto',
            }}
          >
            <Stack
              direction="row"
              sx={{ gap: 2, alignItems: 'center', justifyContent: 'space-between' }}
            >
              {scores.length === 0 ? null : (
                <SegmentedControl<number>
                  label="Platform"
                  role="tab"
                  value={network?.networkId ?? -1}
                  onChange={busy ? () => {} : openNetwork}
                  options={scores.map((row) => ({ id: row.networkId, label: row.label }))}
                />
              )}

              <RunNavigator runs={runs} at={runAt} busy={busy} onSelect={openRun} />
            </Stack>

            <Stack direction="row" sx={{ gap: 3, alignItems: 'stretch', flexWrap: 'wrap' }}>
              {halves === null ? null : <CreativeScorePanel halves={halves} />}
              {network === undefined ? null : (
                <AdjustConfiguration
                  key={runs[runAt]?.runId ?? network.networkId}
                  platform={network.slug}
                  scoringMode={scoringModeOf(batch)}
                  kpi={primaryKpi(batch)}
                  entered={entered}
                  busy={busy}
                  {...(failed === null ? {} : { error: failed })}
                  onRescore={rescore}
                />
              )}
            </Stack>

            {network === undefined ? null : (
              <>
                <RecommendationsSection
                  result={batch}
                  creativeId={creative.creativeId}
                  networkId={network.networkId}
                />
                <CreativeAttributesAnalysis
                  result={batch}
                  creativeId={creative.creativeId}
                  networkId={network.networkId}
                  platformSlug={network.slug}
                />
                <BrandGuidelinesSection
                  result={batch}
                  creativeId={creative.creativeId}
                  networkId={network.networkId}
                />
              </>
            )}
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}
